import axios from 'axios'

/*
 * In-app support (#664).
 *
 * One open conversation at a time, plus the caller's history. The message flow is store-driven so
 * the panel, the launcher badge and the menu count all read the same state.
 *
 * `conversation === undefined` means "not loaded yet" and `null` means "loaded, nothing open" -
 * the panel shows its empty state only for the latter, so it does not flash "How can we help?"
 * while the first request is in flight.
 */
const state = {
  open: false,
  loaded: false,
  conversation: undefined,
  messages: [],
  conversations: [],
  view: 'conversation', // 'conversation' | 'history'
  sending: false,
  loadingHistory: false,
  error: undefined,
}

const mutations = {
  SET_OPEN(state, open) {
    state.open = open
  },
  SET_VIEW(state, view) {
    state.view = view
  },
  SET_CONVERSATION(state, { conversation, messages }) {
    state.conversation = conversation
    state.messages = messages || []
    state.loaded = true
  },
  APPEND_MESSAGE(state, message) {
    state.messages.push(message)
  },
  SET_CONVERSATIONS(state, conversations) {
    state.conversations = conversations || []
  },
  SET_SENDING(state, sending) {
    state.sending = sending
  },
  SET_LOADING_HISTORY(state, loading) {
    state.loadingHistory = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  MARK_READ(state) {
    if (state.conversation) {
      state.conversation.unreadByUser = 0
    }

    state.conversations = state.conversations.map(conversation => ({ ...conversation, unreadByUser: 0 }))
  },
}

const actions = {
  async load({ commit }) {
    const { data } = await axios.get('/api/support/conversation')
    commit('SET_CONVERSATION', { conversation: data.conversation, messages: data.messages })
  },

  async loadHistory({ commit }) {
    commit('SET_LOADING_HISTORY', true)

    try {
      const { data } = await axios.get('/api/support/conversations/mine')
      commit('SET_CONVERSATIONS', data.conversations)
    } finally {
      commit('SET_LOADING_HISTORY', false)
    }
  },

  async openConversation({ commit }, id) {
    const { data } = await axios.get('/api/support/conversations/' + id + '/messages')
    commit('SET_CONVERSATION', { conversation: data.conversation, messages: data.messages })
    commit('SET_VIEW', 'conversation')
  },

  async send({ commit, state }, { body, context, name, email }) {
    commit('SET_SENDING', true)
    commit('SET_ERROR', undefined)

    try {
      const { data } = await axios.post('/api/support/messages', { body, context, name, email })

      commit('SET_CONVERSATION', {
        conversation: data.conversation,
        messages: state.messages.concat(data.message),
      })

      // The saved message, so the caller can tell whether it actually reached Discord
      // (`relayError` is set server-side when the relay throws).
      return data.message
    } catch (err) {
      commit('SET_ERROR', 'There was a problem sending that. Please try again.')
      return false
    } finally {
      commit('SET_SENDING', false)
    }
  },

  async markRead({ commit }) {
    commit('MARK_READ')

    try {
      await axios.post('/api/support/read')
    } catch (err) {
      // A failed read receipt is not worth surfacing - the badge will reset on the next load.
    }
  },

  async archive({ dispatch }, id) {
    await axios.post('/api/support/conversations/' + id + '/archive')
    await dispatch('load')
    await dispatch('loadHistory')
  },

  async openSupport({ commit, dispatch }) {
    commit('SET_OPEN', true)
    await dispatch('load')
    await dispatch('markRead')
  },

  closeSupport({ commit }) {
    commit('SET_OPEN', false)
  },

  async showHistory({ commit, dispatch }) {
    commit('SET_VIEW', 'history')
    await dispatch('loadHistory')
  },

  showConversation({ commit }) {
    commit('SET_VIEW', 'conversation')
  },
}

const getters = {
  isOpen: state => state.open,
  isLoaded: state => state.loaded,
  view: state => state.view,
  conversation: state => state.conversation,
  messages: state => state.messages,
  conversations: state => state.conversations,
  sending: state => state.sending,
  loadingHistory: state => state.loadingHistory,
  // The open conversation holds the count; in the history view, the total across conversations is
  // the useful number for the launcher badge.
  unread: state => {
    if (state.conversation) {
      return state.conversation.unreadByUser || 0
    }

    return state.conversations.reduce((total, conversation) => total + (conversation.unreadByUser || 0), 0)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
